import React, { useState } from 'react'
import { FileText, RefreshCw } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useJobberIntegrations } from '@/features/integrations/hooks/useIntegrations'
import { JobberHeader } from './dashboard/JobberHeader'
import { JobberStatusCard } from './dashboard/JobberStatusCard'
import { JobberSyncStats } from './dashboard/JobberSyncStats'
import { JobberOverview } from './dashboard/JobberOverview'
import { JobberCustomerList } from './dashboard/JobberCustomerList'
import { JobberJobList } from './dashboard/JobberJobList'

// Mock data for Jobber sync statistics
const mockJobberStats = {
  customers: {
    total: 1247,
    synced: 1203,
    pending: 44,
    errors: 0,
    lastSync: '2025-06-10T10:30:00Z'
  },
  jobs: {
    total: 856,
    synced: 823,
    pending: 28,
    errors: 5,
    lastSync: '2025-06-10T10:25:00Z'
  },
  invoices: {
    total: 634,
    synced: 628,
    pending: 4,
    errors: 2,
    lastSync: '2025-06-10T10:35:00Z'
  },
  estimates: {
    total: 432,
    synced: 425,
    pending: 6,
    errors: 1,
    lastSync: '2025-06-10T10:28:00Z'
  }
}

const mockJobberCustomers = [
  {
    id: 'cust_001',
    jobber_id: '12345',
    first_name: 'John',
    last_name: 'Smith',
    email: 'john@email.com',
    phone: '416-555-0123',
    address: '123 Main St, Toronto, ON',
    status: 'active',
    created_in_jobber: '2024-12-15T10:00:00Z',
    sync_status: 'synced',
    total_jobs: 3,
    total_value: 5650
  },
  {
    id: 'cust_002',
    jobber_id: '12346',
    first_name: 'Sarah',
    last_name: 'Johnson',
    email: 'sarah@email.com',
    phone: '416-555-0124',
    address: '456 Oak Ave, Hamilton, ON',
    status: 'active',
    created_in_jobber: '2024-12-10T14:30:00Z',
    sync_status: 'pending',
    total_jobs: 1,
    total_value: 2800
  },
  {
    id: 'cust_003',
    jobber_id: '12347',
    first_name: 'Mike',
    last_name: 'Wilson',
    email: 'mike@email.com',
    phone: '905-555-0125',
    address: '789 Pine Rd, Brampton, ON',
    status: 'active',
    created_in_jobber: '2024-12-08T09:15:00Z',
    sync_status: 'synced',
    total_jobs: 5,
    total_value: 8900
  }
]

const mockJobberJobs = [
  {
    id: 'job_001',
    jobber_id: 'job_12345',
    title: 'Brick Restoration - Main Street',
    customer_name: 'John Smith',
    status: 'completed',
    scheduled_start: '2025-01-15T09:00:00Z',
    actual_end: '2025-01-17T16:00:00Z',
    total_amount: 4500,
    sync_status: 'synced'
  },
  {
    id: 'job_002',
    jobber_id: 'job_12346',
    title: 'Chimney Repair - Oak Avenue',
    customer_name: 'Sarah Johnson',
    status: 'in_progress',
    scheduled_start: '2025-01-20T08:00:00Z',
    total_amount: 2800,
    sync_status: 'synced'
  },
  {
    id: 'job_003',
    jobber_id: 'job_12347',
    title: 'Tuckpointing - Pine Road',
    customer_name: 'Mike Wilson',
    status: 'scheduled',
    scheduled_start: '2025-01-25T09:30:00Z',
    total_amount: 3200,
    sync_status: 'pending'
  }
]

export function JobberManagementContainer() {
  const [activeTab, setActiveTab] = useState('overview')
  const { jobberIntegrations, isLoading } = useJobberIntegrations()

  // We assume only one Jobber integration is active for now
  const connectedIntegration = jobberIntegrations.find(i => ['connected', 'syncing'].includes(i.status))
  const currentIntegration = connectedIntegration || jobberIntegrations[0]
  const isConnected = Boolean(connectedIntegration)

  const handleSync = () => {
    // Logic to trigger sync
    console.log('Sync Jobber')
  }

  const handleConnect = () => {
    // Logic to open connection modal or redirect
    console.log('Connect Jobber')
  }

  if (isLoading) {
    return <div className="p-6 text-center">Loading Jobber integration...</div>
  }

  if (!isConnected) {
    return (
      <div className="space-y-6">
        <JobberHeader onSync={handleSync} isSyncing={false} />
        <JobberStatusCard integration={undefined} onConnect={handleConnect} />
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground space-y-2">
            <p className="text-base">Connect Jobber to view synced customers, jobs, invoices, and sync status.</p>
            <p className="text-sm text-muted-foreground">No Jobber data will appear until a connection is established.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <JobberHeader onSync={handleSync} isSyncing={connectedIntegration?.status === 'syncing'} />

      <JobberStatusCard 
        integration={currentIntegration} 
        onConnect={handleConnect} 
      />

      <JobberSyncStats stats={mockJobberStats} />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="customers">Customers</TabsTrigger>
          <TabsTrigger value="jobs">Jobs</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="sync">Sync Status</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <JobberOverview />
        </TabsContent>

        <TabsContent value="customers" className="space-y-6">
          <JobberCustomerList customers={mockJobberCustomers} />
        </TabsContent>

        <TabsContent value="jobs" className="space-y-6">
          <JobberJobList jobs={mockJobberJobs} />
        </TabsContent>

        <TabsContent value="invoices">
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Invoices Coming Soon</h3>
              <p className="text-muted-foreground">
                Invoice management and tracking will be available in the next update.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sync">
          <Card>
            <CardContent className="p-12 text-center">
              <RefreshCw className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Sync Status Details</h3>
              <p className="text-muted-foreground">
                Detailed sync status and error reporting will be available soon.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
